/**
 * scheduleToEn — describe AWS EventBridge schedule expressions in English.
 *
 * English counterpart of scheduleToZh (cronToZh.ts): same input forms
 * (cron / at / rate), same UTC -> UTC+8 conversion. Output examples:
 *   cron(0 1 * * ? *)          -> "Every day at 09:00"
 *   cron(0 1 ? * MON-FRI *)    -> "Every Mon–Fri at 09:00"
 *   cron(0/15 * * * ? *)       -> "Every day, every 15 minutes"
 *   rate(5 minutes)            -> "Every 5 minutes"
 *   at(2026-04-24T14:33:39)    -> "Once on Apr 24 14:33"
 * Unrecognised input is returned unchanged.
 */
import {
  type CronItem,
  computeDayShift,
  convertHourStr,
  cronItems,
  isSingleValue,
  isWild,
  minuteListItems,
  monthNumber,
  shiftedDowIndex,
} from './cronShared';

const DOW_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const NTH_EN = ['', 'first', 'second', 'third', 'fourth', 'fifth'];

export function scheduleToEn(expr: string): string {
  if (!expr) return '-';
  try {
    const atMatch = expr.match(/^at\((.+)\)$/);
    if (atMatch) return translateAt(atMatch[1]);

    const rateMatch = expr.match(/^rate\((\d+)\s+(minute|hour|day)s?\)$/i);
    if (rateMatch) return translateRate(parseInt(rateMatch[1], 10), rateMatch[2].toLowerCase());

    const cronMatch = expr.match(/^cron\((.+)\)$/);
    if (cronMatch) return translateCron(cronMatch[1].trim());

    return expr;
  } catch {
    return expr;
  }
}

// ── at() / rate() ───────────────────────────────────────────────────────────

function translateAt(timeStr: string): string {
  const d = new Date(timeStr + 'Z');
  if (isNaN(d.getTime())) return `at(${timeStr})`;
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `Once on ${MONTH_EN[d.getUTCMonth()]} ${d.getUTCDate()} ${hh}:${mm}`;
}

function translateRate(n: number, unit: string): string {
  return n === 1 ? `Every ${unit}` : `Every ${n} ${unit}s`;
}

// ── cron() ──────────────────────────────────────────────────────────────────

function translateCron(cronBody: string): string {
  const parts = cronBody.split(/\s+/);
  if (parts.length !== 6) return `cron(${cronBody})`;

  const [minute, hour, dom, month, dow, year] = parts;
  const dayShift = computeDayShift(hour);

  const datePart = describeDate(dom, month, dow, dayShift);
  const timePart = describeTime(minute, hour);
  const yearPart = describeYear(year);

  // "Every day at 09:00" reads without a comma; "Every day, every 15 minutes" needs one.
  const dateAndTime = timePart.startsWith('at ') ? `${datePart} ${timePart}` : `${datePart}, ${timePart}`;
  return [dateAndTime, yearPart].filter(Boolean).join(' ');
}

// ── Time (minute + hour) ────────────────────────────────────────────────────

function describeTime(minute: string, hour: string): string {
  if (isWild(hour)) {
    if (isWild(minute)) return 'every minute';
    const items = cronItems(minute);
    if (items.length === 1 && items[0].step) return everyNMinutes(items[0]);
    if (isSingleValue(minute)) return `at minute ${minute} of every hour`;
    return `at minutes ${minuteListItems(minute).map(minuteItem).join(', ')} of every hour`;
  }

  const single = isSingleValue(minute) ? minute.padStart(2, '0') : null;
  const hours = cronItems(hour);

  if (hours.length === 1 && hours[0].step) {
    const { start, end, step } = hours[0];
    const from = convertHourStr(start);
    if (single) {
      const to = end ? ` to ${convertHourStr(end)}:${single}` : '';
      return `every ${step} hours from ${from}:${single}${to}`;
    }
    const to = end ? ` to ${convertHourStr(end)}:59` : '';
    return `${minutePhrase(minute)}, every ${step} hours from ${from}:00${to}`;
  }
  if (hours.length === 1 && hours[0].end) {
    const s = convertHourStr(hours[0].start);
    const e = convertHourStr(hours[0].end);
    if (single) return `at :${single} past each hour from ${s} to ${e}`;
    return `${minutePhrase(minute)} from ${s}:00 to ${e}:59`;
  }
  if (hours.length > 1) {
    const plain = hours.every((h) => !h.end && !h.step);
    if (single && plain) return `at ${hours.map((h) => `${convertHourStr(h.start)}:${single}`).join(', ')}`;
    const labels = hours.map(hourItem).join(', ');
    if (single) return `at :${single} past hours ${labels}`;
    return `${minutePhrase(minute)} during hours ${labels}`;
  }

  const h = convertHourStr(hour);
  if (single) return `at ${h}:${single}`;
  const minutes = cronItems(minute);
  if (minutes.length > 1 && minutes.every((m) => isSingleValue(m.start) && !m.end && !m.step)) {
    return `at ${minutes.map((m) => `${h}:${m.start.padStart(2, '0')}`).join(', ')}`;
  }
  return `${minutePhrase(minute)} during hour ${h}`;
}

/** One item of an hour list, e.g. "09", "11–13", "every 2 hours from 08", "16–01 every 2 hours". */
function hourItem({ start, end, step }: CronItem): string {
  const range = end ? `${convertHourStr(start)}–${convertHourStr(end)}` : convertHourStr(start);
  if (!step) return range;
  return end ? `${range} every ${step} hours` : `every ${step} hours from ${range}`;
}

function everyNMinutes({ start, end, step }: CronItem): string {
  if (end) return `every ${step} minutes from minute ${start} to ${end}`;
  return start === '0' || start === '*' ? `every ${step} minutes` : `every ${step} minutes from minute ${start}`;
}

/** One item of a minute list (steps already spelled out), e.g. "5", "10–20". */
function minuteItem(item: CronItem): string {
  return item.end ? `${item.start}–${item.end}` : item.start;
}

/** Minute field that is not a single value: wildcard, interval, list or range. */
function minutePhrase(minute: string): string {
  if (isWild(minute)) return 'every minute';
  const items = cronItems(minute);
  if (items.length === 1 && items[0].step) return everyNMinutes(items[0]);
  if (items.length === 1 && items[0].end) return `every minute from minute ${items[0].start} to ${items[0].end}`;
  return `at minutes ${minuteListItems(minute).map(minuteItem).join(', ')}`;
}

// ── Date (dom + month + dow) ────────────────────────────────────────────────

function describeDate(dom: string, month: string, dow: string, dayShift: number): string {
  const hasDom = !isWild(dom);
  const hasDow = !isWild(dow);
  const hasMonth = !isWild(month);
  const monthPart = describeMonth(month);
  const inMonth = hasMonth ? ` in ${monthPart}` : '';

  // Every day, optionally limited to some months ("Every day in Jun")
  if (!hasDom && !hasDow) return `Every day${inMonth}`;

  const next = dayShift > 0 ? ' (next day)' : '';
  const ordinal = hasDow ? describeOrdinalDow(dow) : null;

  if (!hasDom && hasDow) {
    if (ordinal) return `On the ${ordinal} of every month${inMonth}${next}`;
    return `Every ${describeDow(dow, dayShift)}${inMonth}`;
  }

  if (hasDom && !hasDow) {
    const items = cronItems(dom);
    if (items.length === 1 && items[0].step) {
      const { start, end, step } = items[0];
      const to = end ? ` to ${end}` : '';
      return `Every ${step} days from day ${start}${to}${hasMonth ? inMonth : ' of every month'}`;
    }
    const domPart = describeDom(dom);
    return hasMonth ? `On ${domPart} in ${monthPart}` : `Monthly on ${domPart}`;
  }

  // Both DOM and DOW specified (AWS allows this)
  const dowText = ordinal ? `the ${ordinal}${next}` : describeDow(dow, dayShift);
  return `On ${describeDom(dom)} and ${dowText}${inMonth}`;
}

function describeMonth(month: string): string {
  return cronItems(month)
    .map(({ start, end, step }) => {
      if (step) return `every ${step} months from ${monthName(start)}${end ? ` to ${monthName(end)}` : ''}`;
      return end ? `${monthName(start)}–${monthName(end)}` : monthName(start);
    })
    .join(', ');
}

function monthName(m: string): string {
  const n = monthNumber(m);
  return Number.isNaN(n) ? m : MONTH_EN[n - 1];
}

function describeDom(dom: string): string {
  if (dom === 'L') return 'the last day';
  if (dom.toUpperCase() === 'LW') return 'the last weekday';

  const wMatch = dom.match(/^(\d+)W$/i);
  if (wMatch) return `the weekday nearest day ${wMatch[1]}`;

  const items = cronItems(dom);
  if (items.length === 1) {
    const { start, end, step } = items[0];
    if (step) return `every ${step} days from day ${start}${end ? ` to ${end}` : ''}`;
    if (end) return `days ${start}–${end}`;
    return `day ${dom}`;
  }
  const days = items.map(({ start, end, step }) => {
    if (step) return `every ${step} days from ${start}${end ? ` to ${end}` : ''}`;
    return end ? `${start}–${end}` : describeSingleDom(start);
  });
  return `days ${days.join(', ')}`;
}

function describeSingleDom(d: string): string {
  if (d === 'L') return 'last';
  const wMatch = d.match(/^(\d+)W$/i);
  return wMatch ? `${wMatch[1]} (nearest weekday)` : d;
}

/**
 * "last Fri" / "second Mon" for L and # forms, else null. These cannot be
 * day-shifted ("2nd Monday + 1 day" is not "2nd Tuesday"); callers add
 * "(next day)" instead.
 */
function describeOrdinalDow(dow: string): string | null {
  const lMatch = dow.match(/^(\w+)L$/i);
  if (lMatch) return `last ${dowName(lMatch[1], 0)}`;

  const hashMatch = dow.match(/^(\w+)#(\d)$/);
  if (hashMatch) {
    const nth = parseInt(hashMatch[2], 10);
    return `${NTH_EN[nth] || `#${nth}`} ${dowName(hashMatch[1], 0)}`;
  }
  return null;
}

function describeDow(dow: string, dayShift: number): string {
  return cronItems(dow)
    .map(({ start, end, step }) => {
      const first = dowName(start, dayShift);
      if (step) return `${step} days from ${first}${end ? ` to ${dowName(end, dayShift)}` : ''}`;
      return end ? `${first}–${dowName(end, dayShift)}` : first;
    })
    .join(', ');
}

function dowName(d: string, shift: number): string {
  const idx = shiftedDowIndex(d, shift);
  return idx < 0 ? d : DOW_EN[idx];
}

// ── Year ────────────────────────────────────────────────────────────────────

function describeYear(year: string): string {
  if (isWild(year)) return '';
  const years = cronItems(year).map(({ start, end, step }) => {
    if (step) return `every ${step} years from ${start}${end ? ` to ${end}` : ''}`;
    return end ? `${start}–${end}` : start;
  });
  return `(${years.join(', ')})`;
}
